/**
 * Site Categories Context
 * Provides category data (titles, subcategories) throughout the app for proper display
 * Uses the deduplicated getSiteCategories from api.js
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { getSiteCategories } from '@/lib/api';

const SiteCategoriesContext = createContext(null);

export const SiteCategoriesProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Uses deduplicated and cached getSiteCategories
        const data = await getSiteCategories();
        setCategories(data);
      } catch (error) {
        console.error('Failed to fetch site categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Helper function to get category title from slug/id
  const getCategoryTitle = (categoryId) => {
    if (!categoryId) return '';
    const category = categories.find(c => c.id === categoryId || c.slug === categoryId);
    return category?.title || categoryId;
  };

  // Helper function to get full category object
  const getCategory = (categoryId) => {
    if (!categoryId) return null;
    return categories.find(c => c.id === categoryId || c.slug === categoryId) || null;
  };

  // Helper function to get subcategory title from slug/id
  const getSubcategoryTitle = (categoryId, subcategoryId) => {
    if (!subcategoryId) return '';
    const category = categories.find(c => c.id === categoryId || c.slug === categoryId);
    if (!category) return subcategoryId.replace(/-/g, ' ');
    
    const subcategory = category.subcategories?.find(
      s => s.id === subcategoryId || s.slug === subcategoryId
    );
    return subcategory?.name || subcategoryId.replace(/-/g, ' ');
  };

  return (
    <SiteCategoriesContext.Provider value={{ 
      categories, 
      loading,
      getCategory,
      getCategoryTitle,
      getSubcategoryTitle
    }}>
      {children}
    </SiteCategoriesContext.Provider>
  );
};

export const useSiteCategories = () => {
  const context = useContext(SiteCategoriesContext);
  if (!context) {
    // Return default helpers if context is not available
    return {
      categories: [],
      loading: false,
      getCategory: () => null,
      getCategoryTitle: (id) => id || '',
      getSubcategoryTitle: (catId, subId) => subId?.replace(/-/g, ' ') || ''
    };
  }
  return context;
};

export default SiteCategoriesContext;
